'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, AlertCircle, Shield, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import { useAuth } from '@/lib/userContext';
import AppLogo from '@/components/ui/AppLogo';

interface LoginFormData {
  email: string;
  password: string;
}

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  agreedToTerms: boolean;
}

const BASE = process.env.NEXT_PUBLIC_BASE_PATH ?? '';

const demoCredentials = [
  { role: 'Admin', label: 'Admin Login', email: 'admin@klugcmms.com', password: 'Admin@123', icon: Shield },
  { role: 'Technician 1', label: 'Technician 1', email: 'john.tech@klugcmms.com', password: 'Tech@123', icon: Wrench },
  { role: 'Technician 2', label: 'Technician 2', email: 'sara.tech@klugcmms.com', password: 'Tech@123', icon: Wrench },
];

export default function LoginForm() {
  const { login } = useAuth();
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);


  const { register: regLogin, handleSubmit: handleLogin, setValue, formState: { errors: loginErrors }, setError: setLoginError } = useForm<LoginFormData>();
  const { register: regRegister, handleSubmit: handleRegister, formState: { errors: regErrors }, setError: setRegError, reset: resetReg, watch } = useForm<RegisterFormData>();
  const watchPassword = watch('password', '');

  const onLogin = async (data: LoginFormData) => {
    
    try {
      const res = await fetch(`${BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setLoginError('email', { message: json.message ?? 'Invalid credentials' });
        return;
      }
      const { user, accessToken, refreshToken } = json.data;
      login(user, accessToken, refreshToken);
      toast.success(`Welcome back, ${user.name}!`);
      window.location.href = `${BASE}/`;
    } catch {
      setLoginError('email', { message: 'Network error — please try again' });
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (data: RegisterFormData) => {
    if (data.password !== data.confirmPassword) {
      setRegError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setRegError('email', { message: json.message ?? 'Registration failed' });
        return;
      }
      toast.success('Account created! Please sign in.');
      resetReg();
      setTab('login');
      setTimeout(() => setValue('email', data.email), 50);
    } catch {
      setRegError('email', { message: 'Network error — please try again' });
    } finally {
      setLoading(false);
    }
  };

  const [quickLoginLoading, setQuickLoginLoading] = useState<string | null>(null);

  const handleQuickLogin = async (email: string, password: string, role: string) => {
    setTab('login');
    setQuickLoginLoading(role);
    setValue('email', email);
    setValue('password', password);
    setLoading(true);

    await new Promise((r) => setTimeout(r, 800));
    try {
      const res = await fetch(`${BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) { toast.error('Quick login failed'); return; }
      const { user, accessToken, refreshToken } = json.data;
      login(user, accessToken, refreshToken);
      toast.success(`Welcome, ${user.name}!`);
      window.location.href = `${BASE}/`;
    } catch {
      toast.error('Network error');
    } finally {
      setQuickLoginLoading(null);
      setLoading(false);
    }
  };

return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <AppLogo variant="light" height={36} />
        </div>

        <div>
          <h1 className="text-display text-white mb-4 leading-tight">
            Maintenance<br />management,<br />unified.
          </h1>
          <p className="text-white/60 text-base max-w-sm">
            Track assets, assign technicians, manage work orders, and monitor parts inventory — all from one platform.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            {[
              { label: 'Work Orders Managed', value: '12,400+' },
              { label: 'Assets Tracked', value: '3,200+' },
              { label: 'Technicians Active', value: '180+' },
              { label: 'Avg. WO Resolution', value: '4.2 hrs' },
            ].map((stat) => (
              <div key={`stat-${stat.label}`} className="bg-white/5 rounded-xl p-4">
                <p className="font-tabular text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-white/50 text-xs mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/30 text-xs">© 2026 KlugCMMS.</p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo variant="dark" height={32} />
          </div>

          {/* Tab Toggle */}
          <div className="flex rounded-xl border border-border bg-secondary p-1 mb-8">
            <button
              type="button"
              onClick={() => setTab('login')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${tab === 'login' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => { setTab('register'); resetReg(); }}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${tab === 'register' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Register
            </button>
          </div>

          {/* LOGIN FORM */}
          {tab === 'login' && (
            <>
              <div className="mb-8">
                <h2 className="text-title-lg text-foreground">Sign in to your account</h2>
                <p className="text-body-md text-muted-foreground mt-1">Enter your credentials to access the platform.</p>
              </div>

              <form onSubmit={handleLogin(onLogin)} className="space-y-5" noValidate>
                <div>
                  <label htmlFor="email" className="block text-label-sm text-muted-foreground mb-1">Email address</label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@organization.com"
                    className={`underline-input${loginErrors.email ? ' error' : ''}`}
                    {...regLogin('email', {
                      required: 'Email address is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                    })}
                  />
                  {loginErrors.email && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <AlertCircle size={13} className="text-error flex-shrink-0" />
                      <p className="text-xs text-error">{loginErrors.email.message}</p>
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="password" className="text-label-sm text-muted-foreground">Password</label>
                    <Link href="/forgot-password" className="text-body-sm text-muted-foreground hover:text-foreground transition-colors">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      className={`underline-input pr-8${loginErrors.password ? ' error' : ''}`}
                      {...regLogin('password', {
                        required: 'Password is required',
                        minLength: { value: 6, message: 'Password must be at least 6 characters' },
                      })}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-secondary transition-colors">
                      {showPassword ? <EyeOff size={16} className="text-muted-foreground" /> : <Eye size={16} className="text-muted-foreground" />}
                    </button>
                  </div>
                  {loginErrors.password && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <AlertCircle size={13} className="text-error flex-shrink-0" />
                      <p className="text-xs text-error">{loginErrors.password.message}</p>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-base">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Signing in...
                    </span>
                  ) : 'Sign In'}
                </button>
              </form>

              {/* Demo Credentials */}
              <div className="mt-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-body-sm text-muted-foreground whitespace-nowrap">Demo accounts</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="flex gap-2">
                  {demoCredentials.map((cred) => (
                    <button
                      key={`cred-${cred.role}`}
                      type="button"
                      disabled={quickLoginLoading !== null}
                      onClick={() => handleQuickLogin(cred.email, cred.password, cred.role)}
                      className="btn-outlined btn-sm flex-1"
                    >
                      {quickLoginLoading === cred.role ? (
                        <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : <cred.icon size={13} />}
                      {cred.label}
                    </button>
                  ))}
                </div>
              </div>

            </>
          )}

          {/* REGISTER FORM */}
          {tab === 'register' && (
            <>
              <div className="mb-8">
                <h2 className="text-title-lg text-foreground">Create worker account</h2>
                <p className="text-body-md text-muted-foreground mt-1">Register as a technician to access your assigned work orders.</p>
              </div>

              <form onSubmit={handleRegister(onRegister)} className="space-y-5" noValidate>
                <div>
                  <label className="block text-label-sm text-muted-foreground mb-1">Full Name <span className="text-error">*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. John Smith"
                    className={`underline-input${regErrors.name ? ' error' : ''}`}
                    {...regRegister('name', { required: 'Full name is required' })}
                  />
                  {regErrors.name && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <AlertCircle size={13} className="text-error flex-shrink-0" />
                      <p className="text-xs text-error">{regErrors.name.message}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-label-sm text-muted-foreground mb-1">Email Address <span className="text-error">*</span></label>
                  <input
                    type="email"
                    placeholder="you@organization.com"
                    className={`underline-input font-tabular${regErrors.email ? ' error' : ''}`}
                    {...regRegister('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                    })}
                  />
                  {regErrors.email && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <AlertCircle size={13} className="text-error flex-shrink-0" />
                      <p className="text-xs text-error">{regErrors.email.message}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-label-sm text-muted-foreground mb-1">Password <span className="text-error">*</span></label>
                  <div className="relative">
                    <input
                      type={showRegPassword ? 'text' : 'password'}
                      placeholder="Min. 6 characters"
                      className={`underline-input pr-8${regErrors.password ? ' error' : ''}`}
                      {...regRegister('password', {
                        required: 'Password is required',
                        minLength: { value: 6, message: 'Min. 6 characters' },
                      })}
                    />
                    <button type="button" onClick={() => setShowRegPassword(!showRegPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-secondary transition-colors">
                      {showRegPassword ? <EyeOff size={16} className="text-muted-foreground" /> : <Eye size={16} className="text-muted-foreground" />}
                    </button>
                  </div>
                  {regErrors.password && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <AlertCircle size={13} className="text-error flex-shrink-0" />
                      <p className="text-xs text-error">{regErrors.password.message}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-label-sm text-muted-foreground mb-1">Confirm Password <span className="text-error">*</span></label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter your password"
                      className={`underline-input pr-8${regErrors.confirmPassword ? ' error' : ''}`}
                      {...regRegister('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (val) => val === watchPassword || 'Passwords do not match',
                      })}
                    />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-secondary transition-colors">
                      {showConfirmPassword ? <EyeOff size={16} className="text-muted-foreground" /> : <Eye size={16} className="text-muted-foreground" />}
                    </button>
                  </div>
                  {regErrors.confirmPassword && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <AlertCircle size={13} className="text-error flex-shrink-0" />
                      <p className="text-xs text-error">{regErrors.confirmPassword.message}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-label-sm text-muted-foreground mb-1">Phone Number</label>
                  <input
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    className="underline-input font-tabular"
                    {...regRegister('phone')}
                  />
                </div>

                <div>
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 rounded border-border accent-primary flex-shrink-0"
                      {...regRegister('agreedToTerms', { required: 'You must agree to the Terms of Service and Privacy Policy' })}
                    />
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      By signing up, you agree to our{' '}
                      <Link href="/terms-of-service" className="text-primary hover:underline">Terms of Service</Link>{' '}and{' '}
                      <Link href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
                    </span>
                  </label>
                  {regErrors.agreedToTerms && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <AlertCircle size={13} className="text-error flex-shrink-0" />
                      <p className="text-xs text-error">{regErrors.agreedToTerms.message}</p>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-base">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Creating account...
                    </span>
                  ) : 'Create Account'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
